import React, { useRef } from 'react';
import Paper from '@material-ui/core/Paper';
import { withStyles } from '@material-ui/core/styles';
import DateTextField from 'components/FormView/Fields/DateField';
import { Cancel as CancelIcon, CheckCircle as CheckCircleIcon } from '@markinson/uicomponents-v2/SvgIcons';
import TextField from '@markinson/uicomponents-v2/TextFieldV1';
import IProductReceiptSearchProps, { IProductReceiptSearchHandle } from './ProductReceiptsSearch.properties';
import inlineStyles from './ProductReceiptsSearch.styles';
import { isNull, isShallowEqual } from 'utils/utils';
import { ENTER_KEY } from 'utils/constants';
import BasicLookupActionField from 'components/FormView/Fields/BasicLookupActionField';
import { IBasicLookupFieldHandle } from '@markinson/uicomponents-v2/BasicLookupField/BasicLookup.properties';
import LoadingButton from 'components/common/LoadingButton';
import { useAsyncClickHandler } from 'utils/hooks';
import { useUpdateProductReceiptContext } from 'api/productReceipts/productReceipts';
import { usePRContextSelector, usePRDispatch } from 'components/ProductReceipts/ProductReceipts.context';
import { cloneDeep } from 'lodash';
import { ProductReceiptsActionTypes } from 'components/ProductReceipts/ProductReceipts.actions';

const LOOKUP_FIELD_FOCUS_DELAY_TIME = 500;
const INVOICE_FIELD_FOCUS_DELAY_TIME = 2000;
const NON_LOOKUP_FIELD_FOCUS_DELAY_TIME = 200;
const UNDO_TIMEOUT = 100;

const ProductReceiptSearch = (props: IProductReceiptSearchProps, ref: React.Ref<IProductReceiptSearchHandle>) => {
    const { classes, loading: loadingProp, formData, onApply } = props;
    const calculateProductReceiptContext = useUpdateProductReceiptContext();
    const warehouseLookupRef = React.useRef<IBasicLookupFieldHandle>(null);
    const entityLookupRef = React.useRef<IBasicLookupFieldHandle>(null);
    const dateLookupRef = React.useRef(null);
    const [form, setFormValues] = React.useState<any>({});
    const [lookupCalculateRequested, setLookupCalculateRequested] = React.useState<boolean>(false);
    const ProductReceiptId = usePRContextSelector<'ProductReceipt'>((state) => state.ProductReceipt);
    const FocusField = usePRContextSelector<'LHSFocusField'>((state) => state.LHSFocusField);
    const previousValueRef = useRef(form.Invoice);
    const contextDispatch = usePRDispatch();
    const invoiceTotalFieldRef = React.useRef(null);

    const loading = React.useMemo(() => loadingProp, [loadingProp]);

    React.useEffect(
        () => {
            if (!loadingProp) {
                focusOnFieldBasedOnChangedField(FocusField);
            }
        },
        [loadingProp, entityLookupRef]
    );

    React.useEffect(
        () => {
            setFormValues({ ...formData });
        },
        [formData]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            isDirty(): boolean {
                const { Note: __, ...transformedFormData } = formData;
                const { Note: _, ...transformedForm } = form;

                return !isShallowEqual(transformedFormData, transformedForm);
            },
            focusOnWarehouseField(): void {
                focusOnFieldBasedOnChangedField('Warehouse');
            }
        })
    );

    const applyCallback = () => {
        if (validationPassed() && onApply) {
            onApply({ ...form });
        }
    };

    const handleUndoButtonClick = () => {
        if (ProductReceiptId === 0) {
            setFormValues({
                ...formData,
                SupplierId: null,
                Freight: 0,
                Invoice: null,
                InvoiceTotal: 0,
                Reference: null,
            });
        } else {
            setFormValues(formData);
        }
    };

    const { onClick: onApplyClick } = useAsyncClickHandler(applyCallback, !loading && !lookupCalculateRequested);
    const { onClick: onUndoClick } = useAsyncClickHandler(handleUndoButtonClick, !loading && !lookupCalculateRequested);

    const updateFormField = (name, value) => {
        if (form[name] !== value) {
            setFormValues((prevForm) => ({
                ...prevForm,
                [name]: value
            }));

            if (value && ['Warehouse', 'SupplierId', 'Date'].includes(name)) {
                calculateProductReceipt(name, value);
            }

        }
    };

    const validationPassed = () => {
        const { Date = '', Warehouse = '', SupplierId = null }: any = form;

        return !isNull(Date) &&
            !isNull(Warehouse) && !isNull(SupplierId);
    };

    const focusOnFieldWithDelay = (fieldRef, FOCUS_DELAY_TIME: number) => {
        setTimeout(
            () => {
                fieldRef?.current?.focus();
            },
            FOCUS_DELAY_TIME
        );
    };

    const focusOnFieldBasedOnChangedField = (ChangedField: string) => {
        switch (ChangedField) {
            case 'Warehouse':
                focusOnFieldWithDelay(warehouseLookupRef, LOOKUP_FIELD_FOCUS_DELAY_TIME);
                break;
            case 'InvoiceTotal':
                focusOnFieldWithDelay(invoiceTotalFieldRef, LOOKUP_FIELD_FOCUS_DELAY_TIME);
                setTimeout(
                    () => {
                        contextDispatch({
                            [ProductReceiptsActionTypes.setLHSFocusField]: 'Warehouse',
                        });
                    },
                    INVOICE_FIELD_FOCUS_DELAY_TIME
                );
                break;
            case 'Date':
                focusOnFieldWithDelay(dateLookupRef, NON_LOOKUP_FIELD_FOCUS_DELAY_TIME);
                break;
            default:
        }
    };

    const calculateProductReceipt = async (ChangedField: string, value) => {
        setLookupCalculateRequested(false);

        const updatedCriteria = cloneDeep(form);
        let updatedValue = value;

        if (!updatedCriteria?.Freight) {
            updatedCriteria.Freight = 0;
        }
        if (!updatedCriteria?.InvoiceTotal) {
            updatedCriteria.InvoiceTotal = 0;
        }

        if (ChangedField === 'InvoiceTotal' && !value) {
            updatedValue = 0;
        }

        const response = await calculateProductReceiptContext.mutateAsync({
            ProductReceiptContext: { ...updatedCriteria, [ChangedField]: updatedValue },
            ProductReceiptId: ProductReceiptId || 0,
            urlQueryParams: {
                ChangedField
            }
        });

        if (response && response?.Status) {
            contextDispatch({
                [ProductReceiptsActionTypes.setProductReceiptContext]: response.ProductReceiptContext?.inlineObject,
            });
            setFormValues({
                ...response?.ProductReceiptContext?.inlineObject,
            });
        }
    };

    const lookupOnBlur = (name: string, value: string) => {
        if (value && value !== form?.[name]) {
            setLookupCalculateRequested(true);
        }
    };

    return (
        <React.Fragment>
            <Paper style={inlineStyles.lookupFieldsPaperContainer} square={true}>
                <BasicLookupActionField
                    className={classes.customerLookupField}
                    label={'Warehouse'}
                    size={'small'}
                    searchScreenTitle={'Warehouse Lookup'}
                    lookupName={'WarehouseEntity'}
                    value={form?.Warehouse?.toString() ?? ''}
                    onSelectedItemChange={(v, inputText) => { updateFormField('Warehouse', v ? v.Code : inputText); }}
                    suppressDescription={true}
                    disabled={loading || ProductReceiptId > 0}
                    ref={warehouseLookupRef}
                    descriptionField={'Label'}
                    onBlur={(v) => lookupOnBlur('Warehouse', v)}
                    onEnterKeyDown={onApplyClick}
                    searchScreenAPIParms={{ Type: 'LOOKUP' }}
                    required={true}
                />
                <BasicLookupActionField
                    className={classes.basicLookupField}
                    label={'Supplier'}
                    size={'large'}
                    lookupName={'Supplier'}
                    value={form.SupplierId}
                    onSelectedItemChange={(v, inputText) => { updateFormField('SupplierId', v ? v.Code : inputText); }}
                    suppressDescription={true}
                    required={true}
                    disabled={loading || ProductReceiptId > 0}
                    ref={entityLookupRef}
                    onEnterKeyDown={onApplyClick}
                    shouldEnterBehaveAsTab={true}
                />
                <TextField
                    label={'Reference'}
                    name={'Reference'}
                    style={inlineStyles.POfield}
                    value={form.Reference || ''}
                    inputProps={{ maxLength: 15 }}
                    disabled={loading}
                    onChange={(t) => {
                        updateFormField('Reference', t.target.value);
                    }}
                    onKeyDown={async ({ keyCode }) => {
                        if (keyCode === ENTER_KEY) {
                            onApplyClick();
                        }
                    }}
                />
                <DateTextField
                    label={'Date'}
                    value={form.Date}
                    style={inlineStyles.dateField}
                    disabled={loading}
                    defaultValue={false}
                    onChange={(v) => { updateFormField('Date', v); }}
                    required={true}
                    fieldRef={dateLookupRef}
                    onKeyDown={({ keyCode }) => {
                        if (keyCode === ENTER_KEY) {
                            if (dateLookupRef.current) {
                                dateLookupRef.current.blur();
                            }
                            onApplyClick();
                        }
                    }}
                />
            </Paper>
            <Paper style={inlineStyles.lookupFieldsPaperContainer} square={true}>
                <TextField
                    label={'Invoice'}
                    name={'Invoice'}
                    style={inlineStyles.POfield}
                    value={form.Invoice || ''}
                    inputProps={{ maxLength: 15 }}
                    disabled={loading || form?.InvoiceReadOnly}
                    onChange={(t) => { updateFormField('Invoice', t.target.value); }}
                    onBlur={(item) => {
                        const value = item?.currentTarget?.value;
                        if (value !== previousValueRef?.current) {
                            calculateProductReceipt('Invoice', value);
                        }
                        previousValueRef.current = value;
                    }}
                    onKeyDown={async ({ keyCode }) => {
                        if (keyCode === ENTER_KEY) {
                            onApplyClick();
                        }
                    }}
                />
                <TextField
                    fieldRef={(reference) => { invoiceTotalFieldRef.current = reference; }}
                    label={'Invoice total'}
                    name={'InvoiceTotal'}
                    style={inlineStyles.POfield}
                    value={form.InvoiceTotal || ''}
                    inputProps={{ type: 'number', maxLength: 15, style: { textAlign: 'right' } }}
                    disabled={loading || form?.InvoiceTotalReadOnly}
                    onChange={(t) => { updateFormField('InvoiceTotal', t.target.value); }}
                    onKeyDown={async ({ keyCode }) => {
                        if (keyCode === ENTER_KEY) {
                            onApplyClick();
                        }
                    }}
                />
                <TextField
                    label={'Freight (Ex. Tax)'}
                    name={'Freight'}
                    style={inlineStyles.POfield}
                    value={form.Freight || ''}
                    inputProps={{ type: 'number', maxLength: 15, style: { textAlign: 'right' } }}
                    disabled={loading}
                    onChange={(t) => { updateFormField('Freight', t.target.value); }}
                    onKeyDown={async ({ keyCode }) => {
                        if (keyCode === ENTER_KEY) {
                            onApplyClick();
                        }
                    }}
                />
            </Paper>
            <Paper style={inlineStyles.commitActionPaperContainer} square={true}>
                <LoadingButton
                    onClick={() => {
                        setTimeout(
                            () => {
                                onUndoClick();
                            },
                            UNDO_TIMEOUT
                        );
                    }}
                    variant={'contained'}
                    disabled={loading}
                    className={classes.commitActionButton}
                    loading={loading}
                    startIcon={<CancelIcon style={{ color: 'red' }} />}>
                    UNDO
                </LoadingButton>
                <LoadingButton
                    onClick={() => {
                        setTimeout(
                            () => {
                                onApplyClick();
                            },
                            0
                        );
                    }}
                    variant={'contained'}
                    disabled={loading || !validationPassed()}
                    className={classes.commitActionButton}
                    loading={loading}
                    startIcon={<CheckCircleIcon style={{ color: 'green' }} />}>
                    APPLY
                </LoadingButton>
            </Paper>
        </React.Fragment>
    );
};

export default withStyles(inlineStyles, { index: 1 })(React.memo(React.forwardRef(ProductReceiptSearch)));
